import * as React from "react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import { connect } from "react-redux";
import axios, { CancelToken } from "axios";
import { AppDispatch } from "app/store";
import { withRouter, RouteComponentProps } from "react-router-dom";
// components
import ProfileDefaultHeader from "../components/profileDefaultHeader";
import MyCollection from "./components/myCollections";
import FollowingCollection from "./components/followingCollections";
import VingleTab from "common/components/vingleTab";
import PageUpdater from "@vingle/wtf-sdk/components/pageUpdater";
// interfaces
import { IAppState } from "app/rootReducer";
import {
  paginatedCollectionsDenormalizer,
  collectionListDenormalizer,
} from "@vingle/wtf-sdk/models/denormalizer";
// actions
import {
  fetchingUserProfile as fetchingUserProfileAction,
  getUserMyCollections as getUserMyCollectionsAction,
  getUserFollowingCollections as getUserFollowingCollectionsAction,
} from "@vingle/wtf-sdk/actions/userProfile";
import { changeUserFollowing as changeUserFollowingAction } from "@vingle/wtf-sdk/actions/common";
import { ActionCreators } from "app/vingleDialog/actions";
import { push } from "connected-react-router";
// helpers
import { VingleURL } from "@vingle/wtf-sdk/url";
import { profileUserSelector } from "@vingle/wtf-sdk/selectors/userProfile";
// interfaces
const styles = require("./collections.scss");

function mapStateToProps(appState: IAppState) {
  const entities = appState.entities;
  return {
    userProfile: {
      ...appState.userProfile,
      collection: {
        ...appState.userProfile.collection,
        myCollection: collectionListDenormalizer(
          appState.userProfile.collection.myCollection,
          entities,
        ),
        followedCollection: paginatedCollectionsDenormalizer(
          appState.userProfile.collection.followedCollection,
          entities,
        ),
      },
      profile: {
        ...appState.userProfile.profile,
        profile: profileUserSelector(appState),
      },
    },
    currentUser: appState.app.currentUser,
  };
}

function mapDispatchToProps(dispatch: AppDispatch) {
  return {
    fetchingUserProfile: (username: string) => {
      dispatch(fetchingUserProfileAction({ username }));
    },
    getUserMyCollections: (params: {
      username: string;
      cancelToken: CancelToken;
    }) => {
      dispatch(getUserMyCollectionsAction(params));
    },
    getUserFollowingCollections: async (params: {
      username: string;
      cancelToken: CancelToken;
      after?: Vingle.PagingValue;
    }) => dispatch(getUserFollowingCollectionsAction(params)),
    changeUserFollowing: (params: {
      userId: Vingle.Id;
      username: string;
      followingState: boolean;
      cancelToken: CancelToken;
    }) => {
      dispatch(changeUserFollowingAction(params));
    },
    openSignUpDialog: () => {
      dispatch(ActionCreators.openSignUpDialog());
    },
    redirectToCollectionShow: (collection: Vingle.Id) => {
      dispatch(
        push(
          new VingleURL.CollectionShow({
            collection,
          }).toString(),
        ),
      );
    },
  };
}

interface ICollectionsProps
  extends RouteComponentProps<Vingle.IMatchParams>,
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {}

class Collections extends React.PureComponent<
  ICollectionsProps & WrappedComponentProps
> {
  private cancelTokenSource = axios.CancelToken.source();

  public componentDidMount() {
    this.fetchUserInfoAndCollections(this.getUsernameFromUrl());
  }

  public componentDidUpdate(prevProps: ICollectionsProps) {
    const { match } = this.props;
    if (
      prevProps.match.params.username &&
      match.params.username !== prevProps.match.params.username
    ) {
      this.cancelTokenSource.cancel();
      this.cancelTokenSource = axios.CancelToken.source();
      this.fetchUserInfoAndCollections(this.getUsernameFromUrl());
    }
  }

  public componentWillUnmount() {
    this.cancelTokenSource.cancel();
  }

  public render() {
    const { userProfile } = this.props;

    if (!userProfile.profile.profile) {
      return null;
    }

    return (
      <div>
        <PageUpdater title={`${userProfile.profile.profile.username}`} />
        <div className={styles.headerWrapper}>
          <ProfileDefaultHeader />
        </div>
        <div className={styles.tabMenuWrapper}>
          {this.renderTabAndContent()}
        </div>
      </div>
    );
  }

  private readonly getUsernameFromUrl = () => {
    const { match } = this.props;
    return match.params && match.params.username ? match.params.username : "";
  };

  private readonly getIsMine = () => {
    const { userProfile, currentUser } = this.props;
    if (userProfile.profile.profile && currentUser) {
      return userProfile.profile.profile.id === currentUser.id;
    }
    return false;
  };

  private readonly renderTabAndContent = () => {
    const { intl } = this.props;
    const tabs = [
      {
        key: "my_collections",
        title: intl.formatMessage({
          id: "profile.collection_my_collection_title",
        }),
        page: this.renderMyCollections,
      },
      {
        key: "following_collections",
        title: intl.formatMessage({
          id: "profile.collection_following_collection_title",
        }),
        default: VingleURL.UserShowFollowingCollections.isSame(
          this.props.location.pathname,
        ),
        page: this.renderFollowingCollections,
      },
    ];

    return <VingleTab tabs={tabs} vingleTabType="main" />;
  };

  private readonly renderMyCollections = () => {
    const { userProfile, redirectToCollectionShow } = this.props;
    return (
      <div className={styles.contentWrapper}>
        <MyCollection
          collections={userProfile.collection.myCollection}
          isMine={this.getIsMine()}
          redirectToCollectionShow={redirectToCollectionShow}
        />
      </div>
    );
  };

  private readonly renderFollowingCollections = () => {
    const { userProfile, redirectToCollectionShow } = this.props;
    return (
      <div className={styles.contentWrapper}>
        <FollowingCollection
          collections={userProfile.collection.followedCollection}
          onLoadMore={this.handleFetchingFollowingCollectionLoadMore}
          redirectToCollectionShow={redirectToCollectionShow}
        />
      </div>
    );
  };

  private readonly handleFetchingFollowingCollectionLoadMore = () => {
    const { getUserFollowingCollections, userProfile } = this.props;
    const after = userProfile.collection.followedCollection.paging.after;
    if (!userProfile.collection.isLoading && !!after) {
      return getUserFollowingCollections({
        username: this.getUsernameFromUrl(),
        after,
        cancelToken: this.cancelTokenSource.token,
      });
    }
  };

  private readonly fetchUserInfoAndCollections = (username: string) => {
    const {
      fetchingUserProfile,
      getUserMyCollections,
      getUserFollowingCollections,
    } = this.props;
    fetchingUserProfile(username);
    getUserMyCollections({
      username,
      cancelToken: this.cancelTokenSource.token,
    });
    getUserFollowingCollections({
      username,
      cancelToken: this.cancelTokenSource.token,
    });
  };
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(injectIntl(Collections)),
);
